import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { getCurrentGatewayUrl } from './environmentDetection';
import { getAuthHeaders } from './authHeaders';

export type ApplicationCredentialSummary = {
  credentialId: string;
  role: string;
  username: string;
  loginUrl?: string;
  isDisabled?: boolean;
};

export async function listApplicationCredentials({
  applicationId,
}: {
  applicationId: string;
}): Promise<
  | { success: true; credentials: ApplicationCredentialSummary[] }
  | { success: false; error: string }
> {
  const authHeaders = await getAuthHeaders();
  const response = await applicationsClient.getCredentials({
    params: { id: applicationId },
    query: { decrypt: false, includeDisabled: true },
    overrideClientOptions: {
      baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
    },
    extraHeaders: authHeaders,
  });

  if (response.status !== 200) {
    return {
      success: false,
      error: 'Failed to fetch application credentials',
    };
  }

  const credentialsArray = response.body.credentials;
  if (!Array.isArray(credentialsArray)) {
    return {
      success: false,
      error: 'Invalid credentials response',
    };
  }

  const credentials = credentialsArray.map(
    (credential): ApplicationCredentialSummary => ({
      credentialId: credential.id,
      role: credential.role,
      username: credential.username,
      loginUrl: credential.loginUrl ?? undefined,
      isDisabled: credential.isDisabled,
    }),
  );

  return { success: true, credentials };
}

export function filterApplicationCredentials({
  credentials,
  nameFilter,
}: {
  credentials: ApplicationCredentialSummary[];
  nameFilter?: string;
}): ApplicationCredentialSummary[] {
  const normalizedFilter = nameFilter?.trim().toLowerCase();
  if (!normalizedFilter) {
    return credentials;
  }

  return credentials.filter((credential) => {
    const haystack = [
      credential.role,
      credential.username,
      credential.loginUrl ?? '',
      credential.credentialId,
    ]
      .join(' ')
      .toLowerCase();

    return haystack.includes(normalizedFilter);
  });
}
